import { tWithKeyAttr as t } from "../../../i18nHelper";
import { AppImage } from "../../appMedia";
import styles from "./videoCallWindow.module.css";
import React, { useEffect, useRef, useState } from "react";
import Peer from "simple-peer";
import incomingCallSound from "../../../sounds/incomingCallSound.wav";
import { useSelector } from "react-redux";
import ModalOverlay from "../modalOverlay/ModalOverlay";
import { toggleMuteMic, toggleVideo } from "../../../utils/utils";
import {
  callEndedSelector,
  chatStateSelector,
  currentUserIdSelector,
} from "../../../services/selectors/chat.selector";
import { useActions } from "../../../services/hooks/useActions";
import { videoCallInitialState } from "../../../services/slices/chat.slice";
import { END_CALL_CODE } from "../../../utils/constants";
import { useAppSelector } from "../../../services/hooks";
import { userSelector } from "../../../services/selectors/user.selectors";
import Modal from "../modal/Modal";
import MediaDevicesErrorPopup from "../mediaDevicesErrorPopup/mediaDevicesErrorPopup";
import { useGetIceServersQuery } from "../../../services/api/utils.api";

function VideoCallWindow() {
  const user = useAppSelector(userSelector);
  const myVideo = useRef<HTMLVideoElement>(null);
  const userVideo = useRef<HTMLVideoElement>(null);
  const peerRef = useRef<Peer.Instance | null>(null);
  const callEnded = useAppSelector(callEndedSelector);
  const chatState = useSelector(chatStateSelector);
  const currentUserId = useAppSelector(currentUserIdSelector);

  const [showMediaDevicesErrorPopup, setShowMediaDevicesErrorPopup] =
    useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);

  const {
    setCallAccepted,
    setCallEnded,
    setUserStream,
    setCall,
    setStartCall,
    setMyStream,
  } = useActions();
  // const videoTracks = chatState?.userStream?.getVideoTracks();

  // console.log(chatState);
  // console.log("Video Tracks:", chatState.myStream?.getVideoTracks());
  // chatState.myStream?.getVideoTracks().forEach((track) => {
  //   console.log("Track State:", track.readyState, "Enabled:", track.enabled);
  // });

  // if (videoTracks?.length === 0) {
  //   console.log("No video tracks available");
  // } else {
  //   console.log("Video tracks found", videoTracks);
  // }
  const userVideoElement = userVideo.current;
  // userVideoElement?.addEventListener("error", (event) => {
  //   console.error("Error with video playback:", event);
  // });

  //get camera access
  useEffect(() => {
    const initializeMedia = async () => {
      if (navigator.mediaDevices?.getUserMedia) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true,
          });
          setMyStream(stream);
        } catch (err) {
          setShowMediaDevicesErrorPopup(true);
          console.error("Error accessing media devices:", err);
        }
      } else {
        setShowMediaDevicesErrorPopup(true);
        console.error(
          "getUserMedia is not supported in this browser or environment."
        );
      }
    };

    initializeMedia();

    return () => {
      cleanupLocalResources();
    };
  }, []);

  useEffect(() => {
    if (myVideo?.current) {
      myVideo.current.srcObject = chatState.myStream as MediaProvider;
    }
    if (userVideo?.current) {
      userVideo.current.srcObject = chatState.userStream as MediaProvider;
    }
    // console.log(
    //   "Is srcObject set?",
    //   userVideo?.current?.srcObject === chatState.userStream
    // );

    // return () => {
    //   // Cleanup: Ensure video is enabled when the component unmounts
    //   if (chatState.myStream) {
    //     chatState.myStream.getVideoTracks().forEach((track) => {
    //       track.enabled = true; // Re-enable the video track
    //     });
    //     setIsVideoEnabled(true); // Reset state for video toggle
    //   }
    // };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myVideo, userVideo, chatState.userStream, chatState.myStream]);
  const { avatar } = chatState.call.from;
  const { data: dynamicIceServers } = useGetIceServersQuery();
  const iceServers = dynamicIceServers?.iceServers ?? [];

  const normalizeName = (value?: string) => value?.trim().toLowerCase() ?? "";

  const getReceiver = () => {
    const receiverById = (chatState.call.from as { userId?: number }).userId;
    if (receiverById) {
      return chatState.chatUsers.find((u) => u.userId === receiverById);
    }

    return chatState.chatUsers.find(
      (u) => normalizeName(u.fullName) === normalizeName(chatState.call.from.name)
    );
  };

  const answerCall = () => {
    setCallEnded(undefined);
    peerRef.current = new Peer({
      initiator: false,
      trickle: false,
      stream: chatState.myStream,
      config: {
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
           { urls: "stun:stun1.l.google.com:19302" },
          { urls: "stun:stun.services.mozilla.com" },
          ...iceServers,
          
        
        ],
      },
    });
    const pc = (peerRef.current as any)._pc as RTCPeerConnection;
    pc?.addEventListener("icecandidate", (e: RTCPeerConnectionIceEvent) => {
      if (e.candidate) {
        console.log("[ICE]", e.candidate.type, e.candidate.candidate);
        // relay = TURN working ✅  |  srflx = STUN public IP  |  host = local IP
      }
    });

    peerRef.current.on("signal", async (data) => {
      const receiver = getReceiver();
      const payload = JSON.stringify(data);
      try {
        if (receiver?.userId) {
          await chatState.connection?.invoke("SendAnswer", receiver.userId, payload);
        } else {
          await chatState.connection?.invoke("SendAnswer", payload);
        }
      } catch (err) {
        // console.error("SendAnswer invocation failed:", err, {
        //   receiver,
        //   offerFrom: chatState.call.from,
        //   payload,
        // });
      }
    });

    peerRef.current.on("stream", (stream) => setUserStream(stream));
    peerRef.current.on("close", () => {
      if (peerRef.current) {
        peerRef.current = null; // Ensure no dangling references
      }
    });
    peerRef.current.on("error", (err) => console.error("Peer error:", err));
    if (chatState?.offer) {
      peerRef.current.signal(JSON.parse(chatState.offer));
    }
  };

  const leaveCall = () => {
    const receiver = getReceiver();

    chatState.connection?.invoke("SendMessage", {
      type: END_CALL_CODE,
      senderId: user.id || currentUserId,
      content: `${END_CALL_CODE}:by:${user.fullName || ""}`,
      receiverId: receiver?.userId,
    });
    clean();
  };

  useEffect(() => {
    if (callEnded) {
      clean();
    }
  }, [callEnded]);

  // endCall if therapist disconnected
  useEffect(() => {
    if (chatState.callAccepted || chatState.call.isReceivingCall) {
      const therapist = chatState.chatUsers.find(
        (u) => u.fullName === chatState.call.from.name
      );
      if (therapist?.online === false) {
        clean();
      }
    }
  }, [chatState.chatUsers]);
  const cleanupLocalResources = () => {
    if (chatState.myStream) {
      chatState.myStream.getTracks().forEach((track) => track.stop());
    }
    if (peerRef.current) {
      peerRef.current.destroy();
      peerRef.current = null;
    }
    if (myVideo.current) {
      myVideo.current.srcObject = null;
    }
    if (userVideo.current) {
      userVideo.current.srcObject = null;
    }
  };

  const clean = () => {
    cleanupLocalResources();
    setCall(videoCallInitialState);
    setCallAccepted(false);
    setStartCall(false);
    setMyStream(undefined);
  };

  return (
    <>
      <div className={`${styles.window} `}>
        {!chatState.callAccepted && (
          <audio src={incomingCallSound} autoPlay={true} />
        )}
        <div className={styles.videoContainer}>
          <h3 className={styles.header}>
            {chatState.call.from.name} {t('CONTACTING')}</h3>

          {chatState.myStream && (
            <>
              <video
                playsInline
                ref={myVideo}
                muted
                autoPlay
                className={styles.myVideo}
              />
              <div className={styles.userVideoWindow}>
                {!chatState.callAccepted ? (
                  <div className={`${styles.outgoingCall}`}>
                    <div className={styles.pulse} />
                    {avatar && (
                      <AppImage
                        src={avatar}
                        alt={"avatar"}
                        className={`${styles.avatar}`}
                      />
                    )}
                  </div>
                ) : (
                  <video
                    playsInline
                    ref={userVideo}
                    autoPlay
                    className={styles.userVideo}
                  />
                )}
                <div className={styles.buttonsBar}>
                  {!chatState.callAccepted ? (
                    <button
                      type={"button"}
                      className={`hover ${styles.btn} ${styles.btn_type_acceptCall}`}
                      onClick={() => {
                        setCallAccepted(true);
                        answerCall();
                      }}
                    ></button>
                  ) : (
                    <>
                      <button
                        type={"button"}
                        className={`hover ${styles.btn} `}
                        onClick={() => {
                          toggleMuteMic(chatState.myStream, setIsMicMuted);
                        }}
                      >
                        <span
                          className={`${styles.btn_type_mute} ${isMicMuted && styles.btn_type_unmute
                            }`}
                        />
                      </button>
                      <button
                        type={"button"}
                        className={`hover ${styles.btn} `}
                        onClick={() => {
                          toggleVideo(chatState.myStream, setIsVideoEnabled);
                        }}
                      >
                        <span
                          className={`${styles.btn_type_mute} ${styles.btn_type_video
                            } ${!isVideoEnabled && styles.btn_type_video_inactive
                            }`}
                        />
                      </button>
                    </>
                  )}

                  <button
                    type={"button"}
                    className={`hover ${styles.btn} ${styles.btn_type_endCall}`}
                    onClick={() => {
                      leaveCall();
                    }}
                  ></button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <ModalOverlay onClick={leaveCall} />
      {showMediaDevicesErrorPopup && (
        <Modal onClose={() => setShowMediaDevicesErrorPopup(false)}>
          <MediaDevicesErrorPopup
            onClose={() => setShowMediaDevicesErrorPopup(false)}
          />
        </Modal>
      )}
    </>
  );
}

export default VideoCallWindow;
